/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ReferencedComponent from './ComponentReference/ReferencedComponent';
import NodeProperties from './Node/NodeProperties';
import NodeReferenceList from './Node/NodeReferenceList';

// tslint:disable-next-line:typedef
const property = cc._decorator.property;

export enum OrderType
{
    Sequential = 0,
    Grouped = 1,
}

// tslint:disable-next-line:typedef
let ccOrderType = cc.Enum(OrderType);
export { ccOrderType };

@cc._decorator.ccclass
export default class LayerManager extends ReferencedComponent
{
    @property(cc.Integer)
    protected startRange: number = 1;

    @property(cc.Integer)
    protected endRange: number = 100;

    @property({ type: ccOrderType })
    protected orderType: OrderType = OrderType.Sequential;

    @property(cc.Node)
    protected topNode: cc.Node = null;

    @property()
    protected runOnStart: boolean = false;

    private _nodeRefereneceLists: NodeReferenceList[] = [];

    protected onLoad(): void
    {
        super.onLoad();

        this._nodeRefereneceLists = this.getComponentsInChildren(NodeReferenceList);
    }

    protected start(): void
    {
        super.start();

        if (this.runOnStart) { this.Execute(); }
    }

    public Execute(): void
    {
        switch (this.orderType)
        {
            case OrderType.Grouped:
                LayerManager.SortGrouped(this._nodeRefereneceLists,
                    this.startRange, this.endRange, this.topNode);
                break;
            case OrderType.Sequential:
            default:
                LayerManager.SortSequentially(this._nodeRefereneceLists,
                    this.startRange, this.endRange, this.topNode);
                break;
        }
    }

    public Clear(): void
    {
        for (let nodeList of this._nodeRefereneceLists)
        {
            for (let currentNode of nodeList.Nodes)
            {
                LayerManager.SetNodeRecursive((node: cc.Node) =>
                {
                    let nodeProperties: NodeProperties = node.getComponent(NodeProperties);
                    node.zIndex = nodeProperties ? nodeProperties.ZIndexValue : 0;
                }, currentNode, this.topNode);
            }
        }
    }

    public static SortSequentially(
        nodeReferenceLists: NodeReferenceList[],
        index: number,
        max: number,
        topNode: cc.Node = null): void
    {
        max++;

        for (let nodeList of nodeReferenceLists)
        {
            for (let currentNode of nodeList.Nodes)
            {
                let nextIndex: number = (++index < max) ? index : max;
                LayerManager.SetNodeRecursive((node: cc.Node) => node.zIndex = nextIndex, currentNode, topNode);
                index++;
            }
        }
    }

    public static SortGrouped(
        nodeReferenceLists: NodeReferenceList[],
        index: number,
        max: number,
        topNode: cc.Node = null): void
    {
        max++;

        for (let nodeList of nodeReferenceLists)
        {
            for (let currentNode of nodeList.Nodes)
            {
                let nextIndex: number = (++index < max) ? index : max;
                LayerManager.SetNodeRecursive((node: cc.Node) => node.zIndex = nextIndex, currentNode, topNode);
            }

            index++;
        }
    }

    public static SetNodeRecursive(
        fnSetNode: (node: cc.Node) => void,
        target: cc.Node,
        topNode: cc.Node = null): void
    {
        if (!target || !topNode || target.uuid === topNode.uuid) { return; }

        fnSetNode(target);

        this.SetNodeRecursive(fnSetNode, target.parent, topNode);
    }
}
